import {cancel, invoke, on} from "@hybird/core/bridge/hybirdBridge";
import {BridgeMethod} from "@hybird/core/bridge/core";

export class FlutterBluetooth extends BridgeMethod {
  bleConnectionState = {}

  connectListen = {};

  constructor() {
    super();
  }

  /**
   * 获取蓝牙适配器当前状态
   * @return{Promise<{String}>}
   */
  async getBluetoothAdapterState() {
    return invoke("getBluetoothAdapterState")
  }

  /**
   * 打开蓝牙
   * @return{Promise<boolean>}
   */
  async bluetoothTureOn() {
    return invoke("bluetoothTureOn")
  }

  /**
   * 蓝牙适配器状态改变注册监听
   *
   * @param callback{function({state: string})} 蓝牙适配器状态改变时触发的回调
   */
  onBluetoothAdapterStateChange(callback) {
    return on("onBluetoothAdapterStateChange", callback, {
      isSend: true
    });
  }

  /**
   * 蓝牙适配器状态改变取消监听
   *
   * @param callback 取消监听的回调
   */
  async offBluetoothAdapterStateChange(deviceId, callback) {
    cancel("onBluetoothAdapterStateChange", {
      params: {
        deviceId
      }, event: callback, isSend: true
    });
  }

  /**
   * 蓝牙设备连接状态注册监听
   *
   * @param deviceId
   * @param callback 蓝牙设备连接状态改变时触发的回调
   */
  onBluetoothConnectionStateChange(deviceId, callback) {
    return on("onBluetoothConnectionStateChange", callback, {
      filter: data => {
        return !data.deviceId || data.deviceId === deviceId
      }
    });
  }

  /**
   * 蓝牙设备连接状态取消监听
   *
   * @param deviceId
   * @param callback 取消监听的回调
   */
  async offBluetoothConnectionStateChange(deviceId, callback) {
    return cancel("onBluetoothConnectionStateChange", {
      params: {
        deviceId
      }, event: callback
    });
  }

  /**
   * @param deviceId
   * @return boolean
   */
  getBLEConnectionState(deviceId) {
    return this.bleConnectionState[deviceId] || false;
  }

  /**
   * 蓝牙设备监听特征值变化
   *
   * @param callback{function({deviceId,serviceId,characteristicId,value})} 取消监听的回调
   */
  async onBluetoothCharacteristicValueChange(callback) {
    return on("onBluetoothCharacteristicValueChange", callback, {isSend: true});
  }

  /**
   * 蓝牙设备取消监听特征值变化
   *
   * @param callback{function?} 取消监听的回调
   */
  async offBluetoothCharacteristicValueChange(callback) {
    return cancel("onBluetoothCharacteristicValueChange", {event: callback, isSend: true});
  }

  /**
   * 蓝牙开始扫描方法
   *
   * @param services 蓝牙设备主 service 的 uuid 列表,可选参数,默认可为空数组
   * @param names
   * @param deviceId 设备id
   * @param timeout
   * @param on{
   * {
   *   onBluetoothDeviceFound:function({deviceId:String, name:String}),
   *   onError:function(),
   *   onDone:function(),
   * }
   * }
   */
  async startBluetoothDevicesDiscovery({services, names, deviceId, timeout, on}) {
    //先停止, 防止有正在执行的任务
    await this.stopBluetoothDevicesDiscovery();

    return invoke("startBluetoothDevicesDiscovery", {
      params: {
        services, names, timeout, deviceId
      }, on
    });
  }

  /**
   * 蓝牙停止扫描方法
   *
   */
  stopBluetoothDevicesDiscovery() {
    return invoke('stopBluetoothDevicesDiscovery');
  }

  /**
   * 蓝牙连接方法
   *
   * @param deviceId webview通过扫描方法获取到的设备蓝牙key
   * @param timeout 连接超时时长设置，单位毫秒
   */
  connectBLEDevice(deviceId, timeout) {
    return invoke("connectBLEDevice", {
      params: {
        deviceId, timeout
      }
    }).then(async result => {
      const getBLEConnectionState = await invoke("getBLEConnectionState", {
        params: {
          deviceId
        }
      })

      this.bleConnectionState[deviceId] = getBLEConnectionState.state;
      this.connectListen[deviceId] = await this.onBluetoothConnectionStateChange(deviceId, data => {
        this.bleConnectionState[deviceId] = data.state;
      });

      return result;
    });
  }

  /**
   * 蓝牙断连方法
   *
   * @param deviceId webview通过扫描方法获取到的设备蓝牙key
   * @param timeout 连接超时时长设置，单位毫秒
   */
  disconnectBLEDevice(deviceId, timeout) {
    return invoke("disconnectBLEDevice", {
      params: {
        deviceId, timeout
      }
    }).then(async result => {
      delete this.bleConnectionState[deviceId];
      delete this.connectListen[deviceId];

      return result;
    });
  }

  /**
   * 获取蓝牙设备所有 service（服务）
   *
   * @param deviceId webview通过扫描方法获取到的设备蓝牙key,必填
   * @return{Promise<
   * [{
   *   "deviceId": String,
   *   serviceId: String,
   *   isPrimary: boolean
   * }]
   * >}
   */
  getBLEDeviceServices(deviceId) {
    return invoke("getBLEDeviceServices", {
      params: {
        deviceId
      }
    });
  }

  /**
   * 获取蓝牙服务内所有的特征值
   *
   * @param deviceId
   * @param serviceId webview通过getBLEDeviceServices方法获取到的服务key,必填
   * @return{Promise<[{
   *   "deviceId": String,
   *   "characteristicId": String,
   *   "serviceId": String,
   *   "properties": {
   *     "read": boolean,
   *     "write":boolean,
   *     "notify": boolean,
   *     "indicate": boolean,
   *   }
   * }]>}
   */
  getBLEDeviceCharacteristics(deviceId, serviceId) {
    return invoke("getBLEDeviceCharacteristics", {
      params: {
        deviceId, serviceId
      }
    });
  }

  /**
   * @return{Promise<Boolean>}
   */
  requestPermissionBluetooth() {
    return invoke("requestPermissionBluetooth");
  }

  /**
   * 启用/关闭低功耗蓝牙设备特征值变化时的notify功能
   *
   * @param deviceId
   * @param serviceId
   * @param characteristicId 蓝牙服务内的特征值,必填
   * @param notify 是否启用低功耗蓝牙设备特征值变化时的notify功能,必填
   */
  notifyBLECharacteristicValueChange(deviceId, serviceId, characteristicId, notify) {
    return invoke("notifyBLECharacteristicValueChange", {
      params: {
        deviceId, serviceId, characteristicId, notify
      }
    });
  }

  /**
   * 读取低功耗蓝牙设备的特征值的二进制数据值
   *
   * @param deviceId
   * @param serviceId
   * @param characteristicId 蓝牙服务内的特征值,必填
   */
  readBLECharacteristicValue(deviceId, serviceId, characteristicId) {
    return invoke("readBLECharacteristicValue", {
      params: {
        deviceId, serviceId, characteristicId
      }
    });
  }

  /**
   * 向低功耗蓝牙设备特征值中写入二进制数据
   *
   * @param deviceId
   * @param serviceId
   * @param characteristicId 蓝牙服务内的特征值,必填
   * @param value 写入的消息数据,必填
   */
  writeBLECharacteristicValue(deviceId, serviceId, characteristicId, value) {
    return invoke("writeBLECharacteristicValue", {
      params: {
        deviceId, serviceId, characteristicId, value
      }
    });
  }

}

export const flutterBluetooth = new FlutterBluetooth();